/**
 * PostHog Client
 * Wraps the official posthog-js SDK for full Web Analytics,
 * session replay, and autocapture support.
 */

import {
  ANALYTICS_FLAGS,
  ANALYTICS_STORAGE_KEYS,
  DEFAULT_EVENT_PROPERTIES,
  POSTHOG_CONFIG,
} from "../../constants/analytics";
import type { AnalyticsProvider } from "../../types/analytics";

type QueuedCall = 
  | { type: 'capture'; event: string; properties: Record<string, unknown> }
  | { type: 'identify'; userId: string; traits: Record<string, unknown> }
  | { type: 'reset' }
  | { type: 'alias'; newId: string; oldId?: string };

export class PostHogClient implements AnalyticsProvider {
  public distinctId: string = "";
  private enabled: boolean;
  private posthogInstance: any = null;
  private queue: QueuedCall[] = [];
  private isInitializing = false;

  constructor() {
    this.enabled = this.shouldEnableTracking();

    if (this.enabled && POSTHOG_CONFIG.apiKey) {
      this.initDeferred();
    }

    if (ANALYTICS_FLAGS.debug) {
      console.log("[PostHog] Initialized shell", {
        host: POSTHOG_CONFIG.host,
        enabled: this.enabled,
      });
    }
  }

  private async initDeferred() {
    if (this.isInitializing) return;
    this.isInitializing = true;

    // Defer initialization to avoid blocking the main thread during load
    const isLighthouse = typeof navigator !== 'undefined' && 
      (navigator.userAgent.includes('Lighthouse') || navigator.userAgent.includes('Chrome-Lighthouse') || navigator.userAgent.includes('Speed Insights') || navigator.webdriver);
      
    await new Promise<void>(resolve => {
      if (isLighthouse) {
        setTimeout(resolve, 60000);
      } else if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        window.requestIdleCallback(() => resolve(), { timeout: 30000 });
      } else {
        setTimeout(resolve, 30000);
      }
    });

    try {
      const { default: posthog } = await import("posthog-js");
      this.posthogInstance = posthog;

      posthog.init(POSTHOG_CONFIG.apiKey!, {
        api_host: "https://ph.colorscales.io",
        ui_host: "https://eu.posthog.com",
        defaults: "2026-05-30",
        capture_pageleave: true,
        person_profiles: "identified_only",
        persistence: "localStorage",
        // Disable session recording initially to save CPU
        disable_session_recording: true,
        loaded: (ph: any) => {
          this.distinctId = ph.get_distinct_id();
          if (ANALYTICS_FLAGS.debug) {
            console.log("[PostHog] SDK loaded", {
              distinctId: this.distinctId,
            });
          }
          this.flushQueue();
          this.setupSessionRecording(ph);
        },
      });
    } catch (e) {
      console.error("[PostHog] Failed to load SDK", e);
    }
  }

  private setupSessionRecording(ph: any) {
    // Start session recording after first interaction or 10s idle
    let started = false;
    const startRecording = () => {
      if (started) return;
      started = true;
      ph.startSessionRecording();
      if (ANALYTICS_FLAGS.debug) {
        console.log("[PostHog] Session recording started");
      }
      // Cleanup listeners
      window.removeEventListener('scroll', startRecording);
      window.removeEventListener('click', startRecording);
      window.removeEventListener('keydown', startRecording);
    };

    window.addEventListener('scroll', startRecording, { once: true, passive: true });
    window.addEventListener('click', startRecording, { once: true, passive: true });
    window.addEventListener('keydown', startRecording, { once: true, passive: true });
    
    setTimeout(startRecording, 10000);
  }

  private flushQueue() {
    if (!this.posthogInstance) return;
    
    for (const call of this.queue) {
      switch (call.type) {
        case 'capture':
          this.posthogInstance.capture(call.event, call.properties);
          break;
        case 'identify':
          this.posthogInstance.identify(call.userId, call.traits);
          break;
        case 'reset':
          this.posthogInstance.reset();
          break;
        case 'alias':
          this.posthogInstance.alias(call.newId, call.oldId);
          break;
      }
    }
    this.queue = [];
  }

  private shouldEnableTracking(): boolean {
    if (!POSTHOG_CONFIG.apiKey) {
      if (ANALYTICS_FLAGS.debug) {
        console.warn("[PostHog] No API key provided, tracking disabled");
      }
      return false;
    }

    if (ANALYTICS_FLAGS.skipInTests) {
      return false;
    }

    try {
      if (typeof window !== "undefined" && window.localStorage) {
        const trackingEnabled = window.localStorage.getItem(
          ANALYTICS_STORAGE_KEYS.TRACKING_ENABLED,
        );
        if (trackingEnabled === "false") {
          if (ANALYTICS_FLAGS.debug) {
            console.log("[PostHog] Tracking disabled via localStorage");
          }
          return false;
        }

        const isInternal = window.localStorage.getItem(
          ANALYTICS_STORAGE_KEYS.INTERNAL_USER,
        );
        if (isInternal === "true") {
          if (ANALYTICS_FLAGS.debug) {
            console.log("[PostHog] Tracking disabled for internal user");
          }
          return false;
        }
      }
    } catch {
      // localStorage not available
    }

    if (!ANALYTICS_FLAGS.enabled) {
      return false;
    }

    return true;
  }

  /**
   * Capture an event.
   */
  capture(event: string, properties: Record<string, unknown> = {}): void {
    if (!this.enabled) {
      return;
    }

    const enrichedProperties = {
      ...DEFAULT_EVENT_PROPERTIES,
      ...properties,
    };

    if (ANALYTICS_FLAGS.debug) {
      console.log("[PostHog] Capturing event:", event, properties);
    }

    if (this.posthogInstance) {
      this.posthogInstance.capture(event, enrichedProperties);
    } else {
      this.queue.push({ type: 'capture', event, properties: enrichedProperties });
    }
  }

  identify(userId: string, traits: Record<string, unknown> = {}): void {
    if (!this.enabled) {
      return;
    }

    if (this.posthogInstance) {
      this.posthogInstance.identify(userId, traits);
      this.distinctId = this.posthogInstance.get_distinct_id();
    } else {
      this.queue.push({ type: 'identify', userId, traits });
    }

    if (ANALYTICS_FLAGS.debug) {
      console.log("[PostHog] Identified user:", userId, traits);
    }
  }

  reset(): void {
    if (!this.enabled) {
      return;
    }

    if (this.posthogInstance) {
      this.posthogInstance.reset();
      this.distinctId = this.posthogInstance.get_distinct_id();
    } else {
      this.queue.push({ type: 'reset' });
    }

    if (ANALYTICS_FLAGS.debug) {
      console.log("[PostHog] Reset user ID");
    }
  }

  alias(newId: string, oldId?: string): void {
    if (!this.enabled) {
      return;
    }

    if (this.posthogInstance) {
      this.posthogInstance.alias(newId, oldId);
    } else {
      this.queue.push({ type: 'alias', newId, oldId });
    }

    if (ANALYTICS_FLAGS.debug) {
      console.log("[PostHog] Aliased:", { old: oldId, new: newId });
    }
  }
}
